import type { InvoiceReceiptData } from "./InvoiceReceiptDocument";

export function isDigitalResourceReceipt(receipt: InvoiceReceiptData): boolean {
  return receipt.type === "digital_resource" || Boolean(receipt.receiptLabel);
}

function formatAccessDuration(days: number | null | undefined): string {
  if (days == null) return "Permanent";
  if (days === 1) return "1 day";
  return `${days} days`;
}

interface DigitalResourceReceiptFieldsProps {
  receipt: InvoiceReceiptData;
  variant?: "inline" | "rows";
}

export function DigitalResourceReceiptFields({
  receipt,
  variant = "inline",
}: DigitalResourceReceiptFieldsProps) {
  if (!isDigitalResourceReceipt(receipt)) return null;

  const patronType =
    receipt.patronTypeNameAtPurchase?.trim() || receipt.patronTypeAtPurchase || "—";
  const expiryLabel = receipt.accessExpiresAt
    ? new Date(receipt.accessExpiresAt).toLocaleDateString(undefined, { dateStyle: "medium" })
    : receipt.accessDurationDays == null
      ? "No expiry (permanent)"
      : "—";

  if (variant === "rows") {
    return (
      <>
        {receipt.receiptLabel ? (
          <div className="flex justify-between gap-4 border-t border-border/60 pt-3">
            <dt className="text-muted-foreground">Resource receipt ID</dt>
            <dd className="font-mono text-right text-xs text-foreground">{receipt.receiptLabel}</dd>
          </div>
        ) : null}
        {receipt.resourceTitle ? (
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Resource</dt>
            <dd className="text-right text-foreground">{receipt.resourceTitle}</dd>
          </div>
        ) : null}
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Patron category</dt>
          <dd className="text-right text-foreground">{patronType}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Access period</dt>
          <dd className="text-right text-foreground">
            {formatAccessDuration(receipt.accessDurationDays ?? null)}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Access expires</dt>
          <dd className="text-right text-foreground">{expiryLabel}</dd>
        </div>
      </>
    );
  }

  return (
    <dl className="mt-3 grid gap-2 border-t border-border/60 pt-3 text-sm">
      {receipt.receiptLabel ? (
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Resource receipt ID</dt>
          <dd className="font-mono text-xs">{receipt.receiptLabel}</dd>
        </div>
      ) : null}
      <div className="flex justify-between gap-4">
        <dt className="text-muted-foreground">Patron category</dt>
        <dd>{patronType}</dd>
      </div>
      <div className="flex justify-between gap-4">
        <dt className="text-muted-foreground">Access</dt>
        <dd>
          {formatAccessDuration(receipt.accessDurationDays ?? null)}
          {receipt.accessExpiresAt
            ? ` · until ${new Date(receipt.accessExpiresAt).toLocaleDateString()}`
            : ""}
        </dd>
      </div>
    </dl>
  );
}
